import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import ToggleDropdown from '../reused-components/SidebarTabs';
import AddProducts from '../reused-components/AddProducts';
import Profile from '../reused-components/Profile';
import smlogo from '../../img/sm-logo.svg';
import profileImage from '../../img/user-profile.png';
import { fetchProductSettings, updateEmailAddresses, fetchEmailAddresses } from '../../services/apiService';
import DarkModeSwitch from "../reused-components/ToggleDarkMode";

export default function Emails() {
    const { productId } = useParams();
    const [product, setProduct] = useState(null);
    const [emailConfigs, setEmailConfigs] = useState({
        generalSystem: {
            name: 'Enter Your Name',
            email: 'Enter Your Email'
        },
        dontReply: {
            name: 'Enter Your Name',
            email: 'Enter Your Email'
        },
        developerAssistance: {
            name: 'Enter Your Name',
            email: 'Enter Your Email'
        },
        technicalSupport: { 
            name: 'Enter Your Name',
            email: 'Enter Your Email'
        },
        setupHappiness: {
            name: 'Enter Your Name',
            email: 'Enter Your Email'
        },
        generalSupport: {
            name: 'Enter Your Name',
            email: 'Enter Your Email'
        }
    });

    // Add refs for tracking the timeout and abort controller
    const saveTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);

    // Fetch product settings
    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await fetchProductSettings(productId);
                setProduct(data);
            } catch (error) {
                console.error('Error fetching Integration Script:', error);
            }
        };
        fetchData();
    }, [productId]);

    // Add new useEffect to fetch email addresses
    useEffect(() => {
        const fetchEmails = async () => {
            try {
                const response = await fetchEmailAddresses(productId);
                if (response && response.emails) {
                    const emailData = response.emails;
                    
                    // Parse the stored email addresses
                    const parseEmailString = (emailString) => {
                        if (!emailString) return { name: '', email: '' };
                        const [name, email] = emailString.split('-');
                        return { name: name || '', email: email || '' };
                    };

                    setEmailConfigs(prev => ({
                        generalSystem: parseEmailString(emailData.system_address),
                        dontReply: parseEmailString(emailData.dontreply_address),
                        developerAssistance: parseEmailString(emailData.developer_address),
                        technicalSupport: parseEmailString(emailData.technical_address),
                        setupHappiness: parseEmailString(emailData.setup_address),
                        generalSupport: parseEmailString(emailData.general_address)
                    }));
                }
            } catch (error) {
                console.error('Error fetching email addresses:', error);
            }
        };

        if (productId) {
            fetchEmails();
        }
    }, [productId]);

    const handleEmailConfigChange = (configKey, field, value) => {
        // Update the state immediately
        setEmailConfigs(prev => ({
            ...prev,
            [configKey]: {
                ...prev[configKey],
                [field]: value
            }
        }));

        // Clear any existing timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // Cancel any pending request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();

        // Set new timeout for auto-save
        saveTimeoutRef.current = setTimeout(async () => {
            try {
                // Check if the value is too short (e.g., less than 2 characters)
                if (value.length < 2) {
                    return; // Don't send request for very short inputs
                }

                // Prepare the data for all fields
                const updatedEmailData = {
                    system_address: `${emailConfigs.generalSystem.name}-${emailConfigs.generalSystem.email}`,
                    dontreply_address: `${emailConfigs.dontReply.name}-${emailConfigs.dontReply.email}`,
                    developer_address: `${emailConfigs.developerAssistance.name}-${emailConfigs.developerAssistance.email}`,
                    technical_address: `${emailConfigs.technicalSupport.name}-${emailConfigs.technicalSupport.email}`,
                    setup_address: `${emailConfigs.setupHappiness.name}-${emailConfigs.setupHappiness.email}`,
                    general_address: `${emailConfigs.generalSupport.name}-${emailConfigs.generalSupport.email}`
                };

                // Update with the new value
                updatedEmailData[configKey === 'generalSystem' ? 'system_address' : 
                               configKey === 'dontReply' ? 'dontreply_address' :
                               configKey === 'developerAssistance' ? 'developer_address' :
                               configKey === 'technicalSupport' ? 'technical_address' :
                               configKey === 'setupHappiness' ? 'setup_address' :
                               'general_address'] = 
                    `${emailConfigs[configKey].name}-${emailConfigs[configKey].email}`;

                const result = await updateEmailAddresses(
                    productId, 
                    updatedEmailData, 
                    abortControllerRef.current.signal
                );

                if (result.success) {
                    // Show success alert
                    alert('Email addresses updated successfully!');
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Error updating email addresses:', error);
                    alert('Failed to update email settings. Please try again.');
                }
            }
        }, 3500);
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    document.title = "Emails | Telemetry";

    return (
        <div className="telemetry-page-wrapper">
            <header className="telemetry-header">
                <div className="telemetry-logo-section">
                    <a href="#"><img src={smlogo} alt="Telemetry" /></a>
                </div>
                <div className="telemetry-top">
                    <h1>Emails</h1>
                    <div className="telemetry-top-right">
                        <DarkModeSwitch />
                        <AddProducts />
                        <Profile image={profileImage} />
                    </div>
                </div>
            </header>

            <main className="telemetry-main">
                <aside className="telemetry-sidebar">
                    <ToggleDropdown tabactive={1} tabactive2={1} />
                </aside>

                <section className="telemetry-content">
                    <div className="telemetry-setting-tab-content">
                        <h2>Addresses</h2>

                        <div className="telemetry-product">
                            <div className="telemetry-add-products">
                                <div ng-transclude="" bis_skin_checked="1">
                                    <div className="email-config-container">
                                        <ul className="list-disc pl-5 space-y-2 text-white">
                                            <li>
                                                Telemetry will automatically send transactional emails related to different events. You can check the full list of emails by clicking the top-right menu and opening the Notifications center.
                                            </li><br />
                                            <li>
                                                This page can help you to set up custom sender and recipient email addresses for the various messages. If you will not configure an address, it would be automatically set to <strong>hiddenpearls@gmail.com.</strong>
                                            </li><br />
                                            <li>
                                                We highly recommend authenticating your domain using DKIM to minimize email deliverability issues. Please ignore this bullet if your domain was already authenticated manually with the help of our support team.
                                            </li>
                                        </ul>
                                    </div>
									<div className="telemetry-email-container" bis_skin_checked="1">
                                    {/* General System Address */}
                                    <div className="address-types">
                                        <div className="step-content">
                                            <span className="step-text">General System Address</span>
                                        </div>
                                        <p class="telemetry-text-right">
                                            {emailConfigs.generalSystem.name} &lt;{emailConfigs.generalSystem.email}&gt;
                                        </p>
                                    </div>
                                    <div className="telemetry-email-block" bis_skin_checked="1">
                                        <div className="telemetry-email-box" bis_skin_checked="1">
                                            <input
                                                type="text"
                                                name="name"
                                                value={emailConfigs.generalSystem.name}
                                                onChange={(e) => handleEmailConfigChange('generalSystem', 'name', e.target.value)}
                                                placeholder="Enter name"
                                            />
                                            <input
                                                type="text"
                                                name="email"
                                                value={emailConfigs.generalSystem.email}
                                                onChange={(e) => handleEmailConfigChange('generalSystem', 'email', e.target.value)}
                                                placeholder="Enter email"
                                            />
                                        </div>
                                    </div>

                                    {/* Don't Reply Address */}
                                    <div className="address-types">
                                        <div className="step-content">
                                            <span className="step-text">Don't Reply</span>
                                        </div>
                                        <p class="telemetry-text-right">
                                            {emailConfigs.dontReply.name} &lt;{emailConfigs.dontReply.email}&gt;
                                        </p>
                                    </div>
                                    <div className="telemetry-email-block" bis_skin_checked="1">
                                        <div className="telemetry-email-box" bis_skin_checked="1">
                                            <input
                                                type="text"
                                                name="name"
                                                value={emailConfigs.dontReply.name}
                                                onChange={(e) => handleEmailConfigChange('dontReply', 'name', e.target.value)}
                                                placeholder="Enter name"
                                            />
                                            <input
                                                type="text"
                                                name="email"
                                                value={emailConfigs.dontReply.email}
                                                onChange={(e) => handleEmailConfigChange('dontReply', 'email', e.target.value)}
                                                placeholder="Enter email"
                                            />
                                        </div>
                                    </div>

                                    {/* Developer Assistance Address */}
                                    <div className="address-types">
                                        <div className="step-content">
                                            <span className="step-text">Developer Personal Assistance</span>
                                        </div>
                                        <p class="telemetry-text-right">
                                            {emailConfigs.developerAssistance.name} &lt;{emailConfigs.developerAssistance.email}&gt;
                                        </p>
                                    </div>
                                    <div className="telemetry-email-block" bis_skin_checked="1">
                                        <div className="telemetry-email-box" bis_skin_checked="1">
                                            <input
                                                type="text"
                                                name="name"
                                                value={emailConfigs.developerAssistance.name}
                                                onChange={(e) => handleEmailConfigChange('developerAssistance', 'name', e.target.value)}
                                                placeholder="Enter name"
                                            />
                                            <input
                                                type="text"
                                                name="email"
                                                value={emailConfigs.developerAssistance.email}
                                                onChange={(e) => handleEmailConfigChange('developerAssistance', 'email', e.target.value)}
                                                placeholder="Enter email"
                                            />
                                        </div>
                                    </div>

                                    {/* Technical Support Address */}
                                    <div className="address-types">
                                        <div className="step-content">
                                            <span className="step-text">Technical Support rep</span>
                                        </div>
                                        <p class="telemetry-text-right">
                                            {emailConfigs.technicalSupport.name} &lt;{emailConfigs.technicalSupport.email}&gt;
                                        </p>
                                    </div>
                                    <div className="telemetry-email-block" bis_skin_checked="1">
                                        <div className="telemetry-email-box" bis_skin_checked="1">
                                            <input
                                                type="text"
                                                name="name"
                                                value={emailConfigs.technicalSupport.name}
                                                onChange={(e) => handleEmailConfigChange('technicalSupport', 'name', e.target.value)}
                                                placeholder="Enter name"
                                            />
                                            <input
                                                type="text"
                                                name="email"
                                                value={emailConfigs.technicalSupport.email}
                                                onChange={(e) => handleEmailConfigChange('technicalSupport', 'email', e.target.value)}
                                                placeholder="Enter email"
                                            />
                                        </div>
                                    </div>

                                     {/* Technical Support Address */}
                                        <div className="address-types">
                                        <div className="step-content">
                                            <span className="step-text">Setup Happiness rep</span>
                                        </div>
                                        <p class="telemetry-text-right">
                                            {emailConfigs.setupHappiness.name} &lt;{emailConfigs.setupHappiness.email}&gt;
                                        </p>
                                    </div>
                                    <div className="telemetry-email-block" bis_skin_checked="1">
                                        <div className="telemetry-email-box" bis_skin_checked="1">
                                            <input
                                                type="text"
                                                name="name"
                                                value={emailConfigs.setupHappiness.name}
                                                onChange={(e) => handleEmailConfigChange('setupHappiness', 'name', e.target.value)}
                                                placeholder="Enter name"
                                            />
                                            <input
                                                type="text"
                                                name="email"
                                                value={emailConfigs.setupHappiness.email}
                                                onChange={(e) => handleEmailConfigChange('setupHappiness', 'email', e.target.value)}
                                                placeholder="Enter email"
                                            />
                                        </div>
                                    </div>

                                {/* General Support Address */}
                                    <div className="address-types">
                                        <div className="step-content">
                                            <span className="step-text">General Support</span>
                                        </div>
                                        <p class="telemetry-text-right">
                                            {emailConfigs.generalSupport.name} &lt;{emailConfigs.generalSupport.email}&gt;
                                        </p>
                                    </div>
                                    <div className="telemetry-email-block" bis_skin_checked="1">
                                        <div className="telemetry-email-box" bis_skin_checked="1">
                                            <input
                                                type="text"
                                                name="name"
                                                value={emailConfigs.generalSupport.name}
                                                onChange={(e) => handleEmailConfigChange('generalSupport', 'name', e.target.value)}
                                                placeholder="Enter name"
                                            />
                                            <input
                                                type="text"
                                                name="email"
                                                value={emailConfigs.generalSupport.email}
                                                onChange={(e) => handleEmailConfigChange('generalSupport', 'email', e.target.value)}
                                                placeholder="Enter email"
                                            />
                                        </div>
                                    </div>
									</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
}
